import { Box, Button, Flex, Input } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { forwardRef, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { updateUserPassword } from '@/services/user-service';

const createFormSchema = (t: (key: string) => string, hasPassword: boolean) =>
  z
    .object({
      currentPassword: hasPassword
        ? z.string().min(1, t('settings.passwordUpdate.errors.currentPasswordRequired'))
        : z.string().optional(),
      newPassword: z
        .string()
        .min(8, t('settings.passwordUpdate.errors.newPasswordMinLength'))
        .regex(/[A-Za-z]/, t('auth.validation.passwordLetters'))
        .regex(/\d/, t('auth.validation.passwordNumbers')),
      confirmPassword: z
        .string()
        .min(1, t('settings.passwordUpdate.errors.confirmPasswordRequired')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('settings.passwordUpdate.errors.passwordsDoNotMatch'),
      path: ['confirmPassword'],
    });

export type PasswordUpdateFormData = {
  currentPassword?: string;
  newPassword: string;
  confirmPassword: string;
};

export interface PasswordUpdateFormProps {
  onSuccess?: () => void;
  onSubmitting?: (isSubmitting: boolean) => void;
  showActions?: boolean;
}

const PasswordUpdateForm = forwardRef<HTMLFormElement, PasswordUpdateFormProps>(
  ({ onSuccess, onSubmitting, showActions = false }, ref) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const hasPassword = user?.hasPassword ?? true;
    const formSchema = createFormSchema(t, hasPassword);

    const {
      register,
      handleSubmit,
      reset,
      watch,
      formState: { errors, isDirty, isValid },
    } = useForm<PasswordUpdateFormData>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      mode: 'onChange',
    });

    // Watch password fields for requirements display
    useEffect(() => {
      const subscription = watch((value) => {
        setNewPassword(value.newPassword || '');
        setConfirmPassword(value.confirmPassword || '');
      });
      return () => subscription.unsubscribe();
    }, [watch]);

    const mutation = useMutation({
      mutationFn: async (data: PasswordUpdateFormData) => {
        return await updateUserPassword({
          currentPassword: data.currentPassword ?? '',
          newPassword: data.newPassword,
          confirmPassword: data.confirmPassword,
        });
      },
      onSuccess: () => {
        reset();
        setNewPassword('');
        setConfirmPassword('');
        toaster.success({
          title: hasPassword
            ? t('settings.passwordUpdate.success.title')
            : t('settings.passwordUpdate.success.addedTitle'),
          description: hasPassword
            ? t('settings.passwordUpdate.success.description')
            : t('settings.passwordUpdate.success.addedDescription'),
        });
        onSuccess?.();
      },
      onError: (error) => {
        console.error('Failed to update password:', error);
        toaster.error({
          title: hasPassword
            ? t('settings.passwordUpdate.errors.title')
            : t('settings.passwordUpdate.errors.addTitle'),
          description: hasPassword
            ? t('settings.passwordUpdate.errors.description')
            : t('settings.passwordUpdate.errors.addDescription'),
        });
      },
    });

    useEffect(() => {
      onSubmitting?.(mutation.isPending);
    }, [onSubmitting, mutation.isPending]);

    const onSubmit = (data: PasswordUpdateFormData) => {
      mutation.mutate(data);
    };

    return (
      <Box as="form" ref={ref} onSubmit={handleSubmit(onSubmit)} maxW="640px" w="full">
        {hasPassword && (
          <Field
            label={t('settings.passwordUpdate.currentPassword')}
            errorText={errors.currentPassword?.message}
            mb={4}
          >
            <Input
              {...register('currentPassword')}
              type="password"
              variant="outline"
              placeholder={t('settings.passwordUpdate.currentPasswordPlaceholder')}
            />
          </Field>
        )}

        <Field
          label={t('settings.passwordUpdate.newPassword')}
          errorText={errors.newPassword?.message}
          mb={4}
        >
          <Input
            {...register('newPassword')}
            type="password"
            variant="outline"
            placeholder={t('settings.passwordUpdate.newPasswordPlaceholder')}
          />
        </Field>

        <PasswordRequirements
          password={newPassword}
          passwordConfirmation={confirmPassword}
          showMatchRequirement={true}
        />

        <Field
          label={t('settings.passwordUpdate.confirmPassword')}
          errorText={errors.confirmPassword?.message}
          mb={4}
          mt={4}
        >
          <Input
            {...register('confirmPassword')}
            type="password"
            variant="outline"
            placeholder={t('settings.passwordUpdate.confirmPasswordPlaceholder')}
          />
        </Field>

        {showActions && (
          <Flex gap={2} mt={4}>
            <Button
              type="submit"
              size="sm"
              loading={mutation.isPending}
              disabled={!isDirty || !isValid}
            >
              {mutation.isPending
                ? hasPassword
                  ? t('settings.passwordUpdate.updating')
                  : t('settings.passwordUpdate.adding')
                : hasPassword
                  ? t('settings.passwordUpdate.updateButton')
                  : t('settings.passwordUpdate.addButton')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!isDirty || mutation.isPending}
              onClick={() => reset()}
            >
              {t('common.cancel')}
            </Button>
          </Flex>
        )}
      </Box>
    );
  }
);

PasswordUpdateForm.displayName = 'PasswordUpdateForm';

export default PasswordUpdateForm;
